import { identities, mailboxes, messages } from './fixtures';
import { catchAllMailboxIds } from './mailbox-addresses';
import type { Identity, Mailbox, MessageDetail, MessageSummary, SanitizedHtmlDocument } from './types';
import {
  canApplyMailRouteApplication,
  isMatchingActiveMailRouteApplication,
  mapMailRouteApplication,
  type MailRouteApplication,
  type MailRouteApplicationResponse,
  type MailRouteApplicationState
} from './route-application';
export {
  canApplyMailRouteApplication,
  isMatchingActiveMailRouteApplication,
  mapMailRouteApplication,
  type MailRouteApplication,
  type MailRouteApplicationResponse,
  type MailRouteApplicationState
} from './route-application';

type ApiMe = {
  user_id: string;
  display_name: string;
  primary_mailbox: string;
  mailbox_ids: string[];
  send_identities: string[];
  csrf_token: string | null;
};

type ApiMailbox = {
  id: string;
  owner: string;
  folders: Record<string, string>;
  display_name?: string | null;
  primary_email?: string | null;
  description?: string | null;
};

type ApiMessageSummary = {
  email_id: string;
  message_id: string | null;
  subject: string | null;
  sent_at: string | null;
  from_addresses: string[];
  mailbox_id: string;
  folder: string;
  delivery_recipient: string;
  route_type: 'exact' | 'alias' | 'catch_all' | 'imap';
  final_label: 'ham' | 'suspicious' | 'spam';
  final_confidence: number;
  suspicious_signals: string[];
  prompt_injection_signals: string[];
  preview: string | null;
};

type ApiSanitizedHtmlDocument = {
  html: string;
  iframe_sandbox: string;
  content_security_policy: string;
};

type ApiMessageDetail = {
  summary: ApiMessageSummary;
  policy_actions: string[];
  safe_signals: string[];
  dangerous_html_tags: string[];
  reply_to_mismatch: boolean;
  zero_width_char_count: number;
  punycode_url_count: number;
  ip_literal_url_count: number;
  javascript_url_count: number;
  hidden_marker_count: number;
  event_handler_count: number;
  external_resource_count: number;
  comment_count: number;
  spam_signals: Record<string, string>;
  sanitized_html: ApiSanitizedHtmlDocument;
  body_text: string | null;
  raw_text: string;
  body_available: boolean;
};

export type WebmailSnapshot = {
  mailboxes: Mailbox[];
  identities: Identity[];
  messages: MessageSummary[];
  source: 'api' | 'fixtures' | 'unauthenticated';
};

type ApiAdminAddressRoute = {
  address: string;
  mailbox: string;
};

type ApiAdminDomainSummary = {
  domain: string;
  catch_all_mailbox?: string | null;
  catch_all_mailboxes?: string[] | null;
  catchAllMailbox?: string | null;
  catchAllMailboxes?: string[] | null;
  exact_route_count: number;
  alias_count: number;
  mailbox_count: number;
};

type ApiAdminDomainPage = {
  domains: ApiAdminDomainSummary[];
  next_cursor: string | null;
};

type ApiAdminDomainDetail = ApiAdminDomainSummary & {
  addresses?: ApiAdminAddressRoute[];
  aliases?: ApiAdminAddressRoute[];
};

type ApiAdminDomainMailboxPage = {
  domain?: string;
  mailboxes: ApiAdminMailbox[];
  next_cursor: string | null;
};

type ApiDomainOnboardingStep = {
  id: string;
  kind: string;
  account_id: string | null;
  region: string | null;
  target: string;
  manual: boolean;
  hosted_zone_id: string | null;
  depends_on: string[];
  details: Record<string, string>;
};

type ApiDomainOnboardingPlan = {
  domain: string;
  region: string;
  ses_account_id: string;
  route53_account_id: string | null;
  steps: ApiDomainOnboardingStep[];
  activation_blockers: string[];
  warnings: string[];
};

type ApiMailRouteDraft = {
  revision: number;
  lifecycle: 'draft_only';
  active: false;
  users: Array<{
    id: string;
    display_name: string;
    primary_mailbox: string;
    mailbox_access: string[];
  }>;
  mailboxes: ApiAdminMailbox[];
  domains: Array<{
    domain: string;
    catch_all_mailbox?: string | null;
    catch_all_mailboxes?: string[] | null;
    catchAllMailbox?: string | null;
    catchAllMailboxes?: string[] | null;
    addresses: ApiAdminAddressRoute[];
    aliases: ApiAdminAddressRoute[];
    identity_policy: { login: 'explicit' | 'routed'; sender: 'explicit' | 'routed' };
  }>;
  send_identities: Record<string, string[]>;
};

type ApiMailboxSetupResult = {
  status: 'draft_ready' | 'principal_pending' | 'principal_conflict';
  principal_status: 'created' | 'existing' | 'pending' | 'conflict';
  initial_password_required: boolean | null;
  draft: ApiMailRouteDraft;
};

type ApiAdminHoneypot = {
  id: string;
  address: string;
  destination_domain: string;
  version: number;
};

type ApiAdminMailbox = {
  id: string;
  owner: string;
  folders: Record<string, string>;
  display_name?: string | null;
  primary_email?: string | null;
  description?: string | null;
};

type ApiIdentityAdminAccount = {
  user_id: string;
  display_name: string;
  primary_mailbox: string;
  mailbox_ids: string[];
  send_identities: string[];
  identity_email: string | null;
  status: IdentityAdminAccountStatus;
};

type ApiIdentityAdminAccountList = {
  accounts: ApiIdentityAdminAccount[];
  account_console_url: string | null;
};

type ApiSelfServiceAccount = {
  user_id: string;
  account_console_url: string | null;
};

type ApiIdentityPasswordResetResult = {
  status: 'password_updated' | 'password_updated_session_cleanup_incomplete';
  user_id: string;
  password_updated: boolean;
  provider_sessions_revoked: boolean;
  local_sessions_revoked: number;
};

type ApiLlmBudgetSettings = {
  monthly_limit_micro_usd: number;
  input_price_micro_usd_per_million: number;
  output_price_micro_usd_per_million: number;
  max_input_tokens_per_request: number;
  version: number;
};

type ApiLlmBudgetSnapshot = {
  period: string;
  settings: ApiLlmBudgetSettings;
  actual_micro_usd: number;
  reserved_micro_usd: number;
  remaining_micro_usd: number;
  model_calls: number;
  skipped_calls: number;
  pending_reservations: number;
  input_tokens: number;
  output_tokens: number;
  conservative_settlements: number;
};

export type MailAdminAddressRoute = {
  address: string;
  mailbox: string;
};

export type MailAdminDomainSummary = {
  domain: string;
  catchAllMailbox: string | null;
  catchAllMailboxes: string[];
  exactRouteCount: number;
  aliasCount: number;
  mailboxCount: number;
};

export type MailAdminDomainPage = {
  domains: MailAdminDomainSummary[];
  nextCursor: string | null;
};

export type DomainOnboardingPlanInput = {
  domain: string;
  region: string;
  sesAccountId: string;
  route53AccountId?: string;
  hostedZoneId?: string;
  automateDns: boolean;
  receiptRuleSet: string;
  runtimeRoleArn: string;
  mailFromSubdomain?: string;
};

export type DomainOnboardingPlan = {
  domain: string;
  region: string;
  sesAccountId: string;
  route53AccountId: string | null;
  steps: Array<{
    id: string;
    kind: string;
    accountId: string | null;
    region: string | null;
    target: string;
    manual: boolean;
    hostedZoneId: string | null;
    dependsOn: string[];
    details: Record<string, string>;
  }>;
  activationBlockers: string[];
  warnings: string[];
};

export type MailRouteDraft = {
  revision: number;
  lifecycle: 'draft_only';
  active: false;
  users: Array<{
    id: string;
    displayName: string;
    primaryMailbox: string;
    mailboxAccess: string[];
  }>;
  mailboxes: MailAdminMailbox[];
  domains: Array<{
    domain: string;
    catchAllMailbox: string | null;
    catchAllMailboxes: string[];
    addresses: MailAdminAddressRoute[];
    aliases: MailAdminAddressRoute[];
    identityPolicy: { login: 'explicit' | 'routed'; sender: 'explicit' | 'routed' };
  }>;
  sendIdentities: Record<string, string[]>;
};

export type MailboxSetupResult = {
  status: 'draft_ready' | 'principal_pending' | 'principal_conflict';
  principalStatus: 'created' | 'existing' | 'pending' | 'conflict';
  initialPasswordRequired: boolean | null;
  draft: MailRouteDraft;
};

export type MailAdminDomainDetail = MailAdminDomainSummary & {
  addresses: MailAdminAddressRoute[];
  aliases: MailAdminAddressRoute[];
};

export type MailAdminMailbox = {
  id: string;
  owner: string;
  folders: Record<string, string>;
  displayName?: string | null;
  primaryEmail?: string | null;
  description?: string | null;
};

export type MailAdminMailboxPage = {
  mailboxes: MailAdminMailbox[];
  nextCursor: string | null;
};

export type MailAdminHoneypot = {
  id: string;
  address: string;
  destinationDomain: string;
  version: number;
};

export type IdentityAdminAccountStatus = 'ready' | 'missing' | 'not_federated' | 'disabled' | 'unavailable';

export type IdentityAdminAccount = {
  userId: string;
  displayName: string;
  primaryMailbox: string;
  mailboxIds: string[];
  sendIdentities: string[];
  identityEmail: string | null;
  status: IdentityAdminAccountStatus;
};

export type IdentityAdminAccountList = {
  accounts: IdentityAdminAccount[];
  accountConsoleUrl: string | null;
};

export type SelfServiceAccount = {
  userId: string;
  accountConsoleUrl: string | null;
};

export type IdentityPasswordResetResult = {
  status: ApiIdentityPasswordResetResult['status'];
  userId: string;
  passwordUpdated: boolean;
  providerSessionsRevoked: boolean;
  localSessionsRevoked: number;
};

export type LlmBudgetSettings = {
  monthlyLimitMicroUsd: number;
  inputPriceMicroUsdPerMillion: number;
  outputPriceMicroUsdPerMillion: number;
  maxInputTokensPerRequest: number;
  version: number;
};

export type LlmBudgetSnapshot = {
  period: string;
  settings: LlmBudgetSettings;
  actualMicroUsd: number;
  reservedMicroUsd: number;
  remainingMicroUsd: number;
  modelCalls: number;
  skippedCalls: number;
  pendingReservations: number;
  inputTokens: number;
  outputTokens: number;
  conservativeSettlements: number;
};

export type MailFolderRuleCondition = {
  field: string;
  operator: string;
  value: string;
};

export type MailFolderRuleInput = {
  mailbox_id: string;
  name: string;
  priority: number;
  enabled: boolean;
  conditions: MailFolderRuleCondition[];
  action: 'move' | 'copy';
  target_folder: string;
  stop_processing: boolean;
};

export type MailFolderRule = MailFolderRuleInput & {
  id: string;
  version: number;
};

export type AssistantResult = {
  id: string;
  kind: string;
  title: string;
  detail: string;
  sourceField: string;
  confirmationId: string | null;
};

type ApiAssistantResponse = {
  intent: string;
  answer_style: string;
  result_count: number;
  results: Array<{
    kind: string;
    message_id: string | null;
    mailbox_id: string | null;
    subject: string | null;
    final_label: string | null;
    event_id: string | null;
    calendar_id: string | null;
    summary: string | null;
    starts_at: string | null;
    ends_at: string | null;
    participants: string[];
    draft_id: string | null;
    confirmation_id: string | null;
    from_identity: string | null;
    to_addresses: string[];
    body_text: string | null;
    location: string | null;
    description: string | null;
    source_field: string;
  }>;
};

type ApiAssistantConfirmationResponse = {
  status: string;
  intent: string;
  confirmation_id: string;
  draft_id: string;
  mailbox_ids: string[];
  calendar_ids: string[];
  write_status: string;
  source_field: string;
};

export type AssistantConfirmation = {
  status: string;
  intent: string;
  confirmationId: string;
  draftId: string;
  writeStatus: string;
  sourceField: string;
};

export type ComposeSendInput = {
  mailboxId: string;
  fromIdentity: string;
  toAddresses: string[];
  subject: string;
  bodyText: string;
};

export type ComposeSendResult = {
  status: string;
  draftId: string;
  mailboxId: string;
  fromIdentity: string;
  recipientCount: number;
  subject: string;
  sourceField: string;
};

type ApiWebmailSendResponse = {
  status: string;
  draft_id: string;
  mailbox_id: string;
  from_identity: string;
  recipient_count: number;
  subject: string;
  source_field: string;
};

type ApiLogoutResponse = {
  status: string;
};

type ApiAuthOptions = {
  password_login: boolean;
  external_login: boolean;
  external_login_label: string;
};

export type AuthOptions = {
  passwordLogin: boolean;
  externalLogin: boolean;
  externalLoginLabel: string;
};

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let csrfToken: string | null = null;
const fixtureFallbackEnabled = import.meta.env?.DEV === true && import.meta.env?.VITE_WEBMAIL_DATA_SOURCE === 'fixtures';

export async function loadAuthOptions(): Promise<AuthOptions> {
  const options = await getJson<ApiAuthOptions>('/api/session/options');

  return {
    passwordLogin: options.password_login,
    externalLogin: options.external_login,
    externalLoginLabel: options.external_login_label
  };
}

export async function loadWebmailSnapshot(): Promise<WebmailSnapshot> {
  try {
    const me = await getJson<ApiMe>('/api/me');
    csrfToken = me.csrf_token;
    const apiMailboxes = await getJson<ApiMailbox[]>('/api/mailboxes');
    const primaryMailbox = apiMailboxes.find((mailbox) => mailbox.id === me.primary_mailbox) ?? apiMailboxes[0];
    const firstFolder = primaryMailbox ? orderedFolders(primaryMailbox.folders)[0] : undefined;
    const folderMessages = primaryMailbox && firstFolder ? await loadMailboxMessages(primaryMailbox.id, firstFolder, 50, 0) : [];

    return {
      mailboxes: apiMailboxes.map(mapMailbox),
      identities: me.send_identities.map(mapIdentity),
      messages: folderMessages,
      source: 'api'
    };
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      csrfToken = null;
      return {
        mailboxes: [],
        identities: [],
        messages: [],
        source: 'unauthenticated'
      };
    }

    if (fixtureFallbackEnabled) {
      return {
        mailboxes,
        identities,
        messages,
        source: 'fixtures'
      };
    }

    throw error;
  }
}

export async function loadMailboxMessages(mailboxId: string, folder: string, limit = 50, offset = 0): Promise<MessageSummary[]> {
  const query = new URLSearchParams({
    folder,
    limit: limit.toString(),
    offset: offset.toString()
  });
  const response = await getJson<ApiMessageSummary[]>(`/api/mailboxes/${encodeURIComponent(mailboxId)}/messages?${query.toString()}`);

  return response.map(mapMessage);
}

export async function loginWebmailSession(primaryEmail: string, password: string, totpCode?: string, recoveryCode?: string): Promise<void> {
  const response = await postJson<ApiMe>(
    '/api/session/login',
    {
      // Keep the legacy field during the transition; the stable login identity is the primary email.
      user_id: primaryEmail,
      primary_email: primaryEmail,
      password,
      ...(totpCode && totpCode.trim().length > 0 ? { totp_code: totpCode.trim() } : {}),
      ...(recoveryCode && recoveryCode.trim().length > 0 ? { recovery_code: recoveryCode.trim() } : {})
    },
    false
  );
  csrfToken = response.csrf_token;
}

function adminListQuery(query: string, limit: number, cursor?: string): string {
  const params = new URLSearchParams({
    limit: limit.toString()
  });
  if (query.trim()) {
    params.set('q', query.trim());
  }
  if (cursor) {
    params.set('cursor', cursor);
  }
  return params.toString();
}

export async function loadMailAdminDomains(query = '', limit = 25, cursor?: string): Promise<MailAdminDomainPage> {
  const response = await getJson<ApiAdminDomainPage>(`/api/admin/domains?${adminListQuery(query, limit, cursor)}`);

  return {
    domains: response.domains.map((domain) => ({
      domain: domain.domain,
      catchAllMailboxes: catchAllMailboxIds(domain),
      catchAllMailbox: catchAllMailboxIds(domain)[0] ?? null,
      exactRouteCount: domain.exact_route_count,
      aliasCount: domain.alias_count,
      mailboxCount: domain.mailbox_count
    })),
    nextCursor: response.next_cursor ?? null
  };
}

export async function loadMailAdminDomainSummary(domain: string): Promise<MailAdminDomainDetail> {
  const response = await getJson<ApiAdminDomainDetail>(`/api/admin/domains/${encodeURIComponent(domain)}/summary`);
  if (!Array.isArray(response.addresses) || !Array.isArray(response.aliases)) {
    throw new Error('invalid_admin_domain_detail');
  }

  return {
    domain: response.domain,
    catchAllMailboxes: catchAllMailboxIds(response),
    catchAllMailbox: catchAllMailboxIds(response)[0] ?? null,
    addresses: response.addresses,
    aliases: response.aliases,
    exactRouteCount: response.exact_route_count,
    aliasCount: response.alias_count,
    mailboxCount: response.mailbox_count
  };
}

export async function loadMailAdminDomainMailboxes(domain: string, query = '', limit = 25, cursor?: string): Promise<MailAdminMailboxPage> {
  const response = await getJson<ApiAdminDomainMailboxPage>(
    `/api/admin/domains/${encodeURIComponent(domain)}/mailboxes?${adminListQuery(query, limit, cursor)}`
  );

  return {
    mailboxes: response.mailboxes.map((mailbox) => ({
      id: mailbox.id,
      owner: mailbox.owner,
      folders: mailbox.folders,
      displayName: mailbox.display_name ?? null,
      primaryEmail: mailbox.primary_email ?? null,
      description: mailbox.description ?? null
    })),
    nextCursor: response.next_cursor ?? null
  };
}

export async function planMailAdminDomain(input: DomainOnboardingPlanInput): Promise<DomainOnboardingPlan> {
  await ensureCsrfToken();
  const response = await postJson<ApiDomainOnboardingPlan>('/api/admin/domains/plan', {
    domain: input.domain,
    region: input.region,
    ses_account_id: input.sesAccountId,
    ...(input.route53AccountId ? { route53_account_id: input.route53AccountId } : {}),
    ...(input.hostedZoneId ? { hosted_zone_id: input.hostedZoneId } : {}),
    automate_dns: input.automateDns,
    receipt_rule_set: input.receiptRuleSet,
    runtime_role_arn: input.runtimeRoleArn,
    ...(input.mailFromSubdomain ? { mail_from_subdomain: input.mailFromSubdomain } : {})
  });

  return {
    domain: response.domain,
    region: response.region,
    sesAccountId: response.ses_account_id,
    route53AccountId: response.route53_account_id,
    steps: response.steps.map((step) => ({
      id: step.id,
      kind: step.kind,
      accountId: step.account_id,
      region: step.region,
      target: step.target,
      manual: step.manual,
      hostedZoneId: step.hosted_zone_id,
      dependsOn: step.depends_on,
      details: step.details
    })),
    activationBlockers: response.activation_blockers,
    warnings: response.warnings
  };
}

export async function loadMailRouteDraft(): Promise<MailRouteDraft> {
  return mapMailRouteDraft(await getJson<ApiMailRouteDraft>('/api/admin/route-draft'));
}

export async function loadMailRouteApplication(): Promise<MailRouteApplication> {
  return mapMailRouteApplication(await getJson<MailRouteApplicationResponse>('/api/admin/route-application'));
}

export async function applyMailRouteApplication(expectedRevision: number): Promise<MailRouteApplication> {
  await ensureCsrfToken();
  return mapMailRouteApplication(await postJson<MailRouteApplicationResponse>('/api/admin/route-application', {
    expected_revision: expectedRevision
  }));
}

export async function createMailRouteDraftMailbox(input: {
  userId: string;
  mailboxId: string;
  folders: Record<string, string>;
  expectedRevision: number;
  accessGrantUserId?: string;
}): Promise<MailRouteDraft> {
  await ensureCsrfToken();
  return mapMailRouteDraft(await postJson<ApiMailRouteDraft>('/api/admin/route-draft/mailboxes', {
    user_id: input.userId,
    mailbox_id: input.mailboxId,
    folders: input.folders,
    expected_revision: input.expectedRevision,
    ...(input.accessGrantUserId ? { access_grant_user_id: input.accessGrantUserId } : {})
  }));
}

export async function setupMailboxPrincipal(input: {
  displayName: string;
  primaryAddress: string;
  description?: string;
  aliases?: string[];
  catchAll?: boolean;
  expectedRevision: number;
  userId?: string;
  mailboxId?: string;
}): Promise<MailboxSetupResult> {
  await ensureCsrfToken();
  const response = await postJson<ApiMailboxSetupResult>('/api/admin/mailbox-setups', {
    display_name: input.displayName,
    primary_address: input.primaryAddress,
    description: input.description ?? '',
    aliases: input.aliases ?? [],
    catch_all: input.catchAll ?? false,
    expected_revision: input.expectedRevision,
    ...(input.userId ? { user_id: input.userId } : {}),
    ...(input.mailboxId ? { mailbox_id: input.mailboxId } : {})
  });
  return {
    status: response.status,
    principalStatus: response.principal_status,
    initialPasswordRequired: response.initial_password_required,
    draft: mapMailRouteDraft(response.draft)
  };
}

export async function updateMailRouteDraftMailboxProfile(input: {
  domain: string;
  mailboxId: string;
  displayName: string;
  primaryAddress: string;
  description: string;
  aliases: string[];
  catchAll: boolean;
  expectedRevision: number;
}): Promise<MailRouteDraft> {
  await ensureCsrfToken();
  return mapMailRouteDraft(await putJson<ApiMailRouteDraft>(
    `/api/admin/route-draft/domains/${encodeURIComponent(input.domain)}/mailboxes/${encodeURIComponent(input.mailboxId)}/profile`,
    {
      display_name: input.displayName,
      primary_address: input.primaryAddress,
      description: input.description,
      aliases: input.aliases,
      catch_all: input.catchAll,
      expected_revision: input.expectedRevision
    }
  ));
}

export async function replaceMailRouteDraftMailboxAddresses(input: {
  domain: string;
  mailboxId: string;
  addresses: string[];
  aliases: string[];
  expectedRevision: number;
}): Promise<MailRouteDraft> {
  await ensureCsrfToken();
  return mapMailRouteDraft(await putJson<ApiMailRouteDraft>(
    `/api/admin/route-draft/domains/${encodeURIComponent(input.domain)}/mailboxes/${encodeURIComponent(input.mailboxId)}`,
    { addresses: input.addresses, aliases: input.aliases, expected_revision: input.expectedRevision }
  ));
}

export async function replaceMailRouteDraftDomain(input: {
  domain: string;
  addresses: MailAdminAddressRoute[];
  aliases: MailAdminAddressRoute[];
  catchAllMailboxes: string[];
  loginPolicy: 'explicit' | 'routed';
  senderPolicy: 'explicit' | 'routed';
  expectedRevision: number;
}): Promise<MailRouteDraft> {
  await ensureCsrfToken();
  return mapMailRouteDraft(await putJson<ApiMailRouteDraft>(
    `/api/admin/route-draft/domains/${encodeURIComponent(input.domain)}`,
    {
      addresses: Object.fromEntries(input.addresses.map((route) => [route.address, route.mailbox])),
      aliases: Object.fromEntries(input.aliases.map((route) => [route.address, route.mailbox])),
      // The plural field is authoritative; retain both spellings while the HTTP DTO rolls out.
      catchAllMailboxes: input.catchAllMailboxes,
      catch_all_mailboxes: input.catchAllMailboxes,
      catch_all_mailbox: input.catchAllMailboxes[0] ?? null,
      identity_policy: { login: input.loginPolicy, sender: input.senderPolicy },
      expected_revision: input.expectedRevision
    }
  ));
}

export async function replaceMailRouteDraftSendIdentities(input: {
  userId: string;
  identities: string[];
  expectedRevision: number;
}): Promise<MailRouteDraft> {
  await ensureCsrfToken();
  return mapMailRouteDraft(await putJson<ApiMailRouteDraft>(
    `/api/admin/route-draft/users/${encodeURIComponent(input.userId)}/send-identities`,
    { identities: input.identities, expected_revision: input.expectedRevision }
  ));
}

export async function loadMailAdminHoneypots(): Promise<MailAdminHoneypot[]> {
  const response = await getJson<ApiAdminHoneypot[]>('/api/admin/honeypots');
  return response.map(mapMailAdminHoneypot);
}

export async function createMailAdminHoneypot(address: string): Promise<MailAdminHoneypot> {
  await ensureCsrfToken();
  const response = await postJson<ApiAdminHoneypot>('/api/admin/honeypots', { address });
  return mapMailAdminHoneypot(response);
}

export async function deleteMailAdminHoneypot(honeypot: MailAdminHoneypot): Promise<void> {
  await ensureCsrfToken();
  await postJson(`/api/admin/honeypots/${encodeURIComponent(honeypot.id)}/delete`, {
    expected_version: honeypot.version
  });
}

export async function loadIdentityAdminAccounts(): Promise<IdentityAdminAccountList> {
  const response = await getJson<ApiIdentityAdminAccountList>('/api/admin/accounts');

  return {
    accounts: response.accounts.map((account) => ({
      userId: account.user_id,
      displayName: account.display_name,
      primaryMailbox: account.primary_mailbox,
      mailboxIds: account.mailbox_ids,
      sendIdentities: account.send_identities,
      identityEmail: account.identity_email,
      status: account.status
    })),
    accountConsoleUrl: response.account_console_url
  };
}

export async function loadSelfServiceAccount(): Promise<SelfServiceAccount> {
  const response = await getJson<ApiSelfServiceAccount>('/api/settings/account');
  return {
    userId: response.user_id,
    accountConsoleUrl: response.account_console_url
  };
}

export async function resetIdentityPassword(userId: string, password: string): Promise<IdentityPasswordResetResult> {
  await ensureCsrfToken();
  const response = await postJson<ApiIdentityPasswordResetResult>(
    `/api/admin/accounts/${encodeURIComponent(userId)}/password`,
    { password }
  );

  return {
    status: response.status,
    userId: response.user_id,
    passwordUpdated: response.password_updated,
    providerSessionsRevoked: response.provider_sessions_revoked,
    localSessionsRevoked: response.local_sessions_revoked
  };
}

export async function loadLlmBudget(): Promise<LlmBudgetSnapshot> {
  return mapLlmBudgetSnapshot(await getJson<ApiLlmBudgetSnapshot>('/api/admin/llm-budget'));
}

export async function updateLlmBudget(settings: LlmBudgetSettings): Promise<LlmBudgetSnapshot> {
  await ensureCsrfToken();
  const response = await putJson<ApiLlmBudgetSnapshot>('/api/admin/llm-budget', {
    monthly_limit_micro_usd: settings.monthlyLimitMicroUsd,
    input_price_micro_usd_per_million: settings.inputPriceMicroUsdPerMillion,
    output_price_micro_usd_per_million: settings.outputPriceMicroUsdPerMillion,
    max_input_tokens_per_request: settings.maxInputTokensPerRequest,
    expected_version: settings.version
  });
  return mapLlmBudgetSnapshot(response);
}

export async function loadMailFolderRules(): Promise<MailFolderRule[]> {
  return getJson<MailFolderRule[]>('/api/admin/folder-rules');
}

export async function createMailFolderRule(input: MailFolderRuleInput): Promise<MailFolderRule> {
  await ensureCsrfToken();
  return postJson<MailFolderRule>('/api/admin/folder-rules', input);
}

export async function replaceMailFolderRule(rule: MailFolderRule, input: MailFolderRuleInput): Promise<MailFolderRule> {
  await ensureCsrfToken();
  return putJson<MailFolderRule>(`/api/admin/folder-rules/${encodeURIComponent(rule.id)}`, {
    expected_version: rule.version,
    rule: input
  });
}

export async function deleteMailFolderRule(rule: MailFolderRule): Promise<void> {
  await ensureCsrfToken();
  await postJson(`/api/admin/folder-rules/${encodeURIComponent(rule.id)}/delete`, {
    expected_version: rule.version
  });
}

export async function loadSelfMailFolderRules(): Promise<MailFolderRule[]> {
  return getJson<MailFolderRule[]>('/api/settings/folder-rules');
}

export async function createSelfMailFolderRule(input: MailFolderRuleInput): Promise<MailFolderRule> {
  await ensureCsrfToken();
  return postJson<MailFolderRule>('/api/settings/folder-rules', input);
}

export async function replaceSelfMailFolderRule(rule: MailFolderRule, input: MailFolderRuleInput): Promise<MailFolderRule> {
  await ensureCsrfToken();
  return putJson<MailFolderRule>(`/api/settings/folder-rules/${encodeURIComponent(rule.id)}`, {
    expected_version: rule.version,
    rule: input
  });
}

export async function deleteSelfMailFolderRule(rule: MailFolderRule): Promise<void> {
  await ensureCsrfToken();
  await postJson(`/api/settings/folder-rules/${encodeURIComponent(rule.id)}/delete`, {
    expected_version: rule.version
  });
}

export async function queryAssistant(query: string): Promise<AssistantResult[]> {
  await ensureCsrfToken();
  const response = await postJson<ApiAssistantResponse>('/api/assistant/query', { query });

  return response.results.map(mapAssistantResult);
}

export async function loadMessageDetail(mailboxId: string, emailId: string): Promise<MessageDetail> {
  const response = await getJson<ApiMessageDetail>(
    `/api/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(emailId)}`
  );

  return mapMessageDetail(response);
}

export async function confirmAssistantDraft(confirmationId: string): Promise<AssistantConfirmation> {
  await ensureCsrfToken();
  const response = await postJson<ApiAssistantConfirmationResponse>('/api/assistant/confirm', {
    confirmation_id: confirmationId
  });

  return {
    status: response.status,
    intent: response.intent,
    confirmationId: response.confirmation_id,
    draftId: response.draft_id,
    writeStatus: response.write_status,
    sourceField: response.source_field
  };
}

export async function sendComposeStub(input: ComposeSendInput): Promise<ComposeSendResult> {
  await ensureCsrfToken();
  const response = await postJson<ApiWebmailSendResponse>(`/api/mailboxes/${encodeURIComponent(input.mailboxId)}/send`, {
    from_identity: input.fromIdentity,
    to_addresses: input.toAddresses,
    subject: input.subject,
    body_text: input.bodyText
  });

  return {
    status: response.status,
    draftId: response.draft_id,
    mailboxId: response.mailbox_id,
    fromIdentity: response.from_identity,
    recipientCount: response.recipient_count,
    subject: response.subject,
    sourceField: response.source_field
  };
}

export async function logoutWebmailSession(): Promise<string> {
  await ensureCsrfToken();
  const response = await postJson<ApiLogoutResponse>('/api/session/logout', {});
  csrfToken = null;
  return response.status;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new ApiError(response.status, `Webmail API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

async function postJson<T>(path: string, body: unknown, withCsrf = true): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(withCsrf && csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new ApiError(response.status, `Webmail API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new ApiError(response.status, `Webmail API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

async function ensureCsrfToken(): Promise<void> {
  if (csrfToken !== null) {
    return;
  }

  const me = await getJson<ApiMe>('/api/me');
  csrfToken = me.csrf_token;
}

function mapMailbox(mailbox: ApiMailbox): Mailbox {
  const displayName = mailbox.display_name?.trim() || null;
  const primaryEmail = mailbox.primary_email?.trim().toLowerCase() || null;
  return {
    id: mailbox.id,
    label: displayName ?? primaryEmail ?? 'Mailbox',
    owner: mailbox.owner,
    folders: mailbox.folders,
    displayName,
    primaryEmail
  };
}

function mapMailRouteDraft(draft: ApiMailRouteDraft): MailRouteDraft {
  if (draft.lifecycle !== 'draft_only' || draft.active !== false) {
    throw new Error('invalid_mail_route_draft_lifecycle');
  }

  return {
    revision: draft.revision,
    lifecycle: draft.lifecycle,
    active: draft.active,
    users: draft.users.map((user) => ({
      id: user.id,
      displayName: user.display_name,
      primaryMailbox: user.primary_mailbox,
      mailboxAccess: user.mailbox_access
    })),
    mailboxes: draft.mailboxes.map((mailbox) => ({
      id: mailbox.id,
      owner: mailbox.owner,
      folders: mailbox.folders,
      displayName: mailbox.display_name ?? null,
      primaryEmail: mailbox.primary_email ?? null,
      description: mailbox.description ?? null
    })),
    domains: draft.domains.map((domain) => ({
      domain: domain.domain,
      catchAllMailboxes: catchAllMailboxIds(domain),
      catchAllMailbox: catchAllMailboxIds(domain)[0] ?? null,
      addresses: domain.addresses,
      aliases: domain.aliases,
      identityPolicy: domain.identity_policy
    })),
    sendIdentities: draft.send_identities
  };
}

function mapMailAdminHoneypot(honeypot: ApiAdminHoneypot): MailAdminHoneypot {
  return {
    id: honeypot.id,
    address: honeypot.address,
    destinationDomain: honeypot.destination_domain,
    version: honeypot.version
  };
}

function mapLlmBudgetSnapshot(snapshot: ApiLlmBudgetSnapshot): LlmBudgetSnapshot {
  return {
    period: snapshot.period,
    settings: {
      monthlyLimitMicroUsd: snapshot.settings.monthly_limit_micro_usd,
      inputPriceMicroUsdPerMillion: snapshot.settings.input_price_micro_usd_per_million,
      outputPriceMicroUsdPerMillion: snapshot.settings.output_price_micro_usd_per_million,
      maxInputTokensPerRequest: snapshot.settings.max_input_tokens_per_request,
      version: snapshot.settings.version
    },
    actualMicroUsd: snapshot.actual_micro_usd,
    reservedMicroUsd: snapshot.reserved_micro_usd,
    remainingMicroUsd: snapshot.remaining_micro_usd,
    modelCalls: snapshot.model_calls,
    skippedCalls: snapshot.skipped_calls,
    pendingReservations: snapshot.pending_reservations,
    inputTokens: snapshot.input_tokens,
    outputTokens: snapshot.output_tokens,
    conservativeSettlements: snapshot.conservative_settlements
  };
}

function mapIdentity(address: string): Identity {
  const domain = address.split('@')[1] ?? '';
  return { address, domain };
}

function mapMessage(message: ApiMessageSummary): MessageSummary {
  const subject = message.subject || '(no subject)';
  const from = message.from_addresses.length > 0 ? message.from_addresses.join(', ') : '(unknown sender)';

  return {
    emailId: message.email_id,
    subject,
    from,
    sentAt: message.sent_at || '',
    mailboxId: message.mailbox_id,
    folder: message.folder,
    deliveryRecipient: message.delivery_recipient,
    routeType: message.route_type,
    finalLabel: message.final_label,
    finalConfidence: message.final_confidence,
    suspiciousSignals: message.suspicious_signals,
    promptInjectionSignals: message.prompt_injection_signals,
    preview: message.preview || `Projection only: ${subject}`,
    sanitizedHtml: projectionPendingDocument()
  };
}

function mapMessageDetail(detail: ApiMessageDetail): MessageDetail {
  return {
    ...mapMessage(detail.summary),
    policyActions: detail.policy_actions,
    safeSignals: detail.safe_signals,
    dangerousHtmlTags: detail.dangerous_html_tags,
    replyToMismatch: detail.reply_to_mismatch,
    zeroWidthCharCount: detail.zero_width_char_count,
    punycodeUrlCount: detail.punycode_url_count,
    ipLiteralUrlCount: detail.ip_literal_url_count,
    javascriptUrlCount: detail.javascript_url_count,
    hiddenMarkerCount: detail.hidden_marker_count,
    eventHandlerCount: detail.event_handler_count,
    externalResourceCount: detail.external_resource_count,
    commentCount: detail.comment_count,
    spamSignals: detail.spam_signals,
    sanitizedHtml: mapSanitizedHtml(detail.sanitized_html),
    bodyText: detail.body_text,
    rawText: detail.raw_text,
    bodyAvailable: detail.body_available
  };
}

function mapAssistantResult(result: ApiAssistantResponse['results'][number]): AssistantResult {
  if (result.kind === 'reply_draft') {
    return {
      id: result.draft_id || `${result.kind}-${result.message_id || 'message'}`,
      kind: result.kind,
      title: result.subject || '(no subject)',
      detail: `${result.from_identity || 'identity'} -> ${result.to_addresses.join(', ') || 'recipient'} | draft only`,
      sourceField: result.source_field,
      confirmationId: result.confirmation_id
    };
  }

  if (result.kind === 'calendar_event_draft') {
    const startsAt = result.starts_at || '(unknown start)';
    const endsAt = result.ends_at || '(unknown end)';
    return {
      id: result.draft_id || `${result.kind}-${startsAt}`,
      kind: result.kind,
      title: result.summary || '(untitled event)',
      detail: `${result.calendar_id || 'calendar'} | ${startsAt} to ${endsAt} | draft only`,
      sourceField: result.source_field,
      confirmationId: result.confirmation_id
    };
  }

  if (result.kind === 'calendar_event' || result.kind === 'busy_slot') {
    const startsAt = result.starts_at || '(unknown start)';
    const endsAt = result.ends_at || '(unknown end)';
    return {
      id: result.event_id || `${result.kind}-${startsAt}`,
      kind: result.kind,
      title: result.summary || '(untitled event)',
      detail: `${result.calendar_id || 'calendar'} | ${startsAt} to ${endsAt}`,
      sourceField: result.source_field,
      confirmationId: null
    };
  }

  return {
    id: result.message_id || `${result.kind}-${result.subject || 'message'}`,
    kind: result.kind,
    title: result.subject || '(no subject)',
    detail: `${result.mailbox_id || 'mailbox'} | ${result.final_label || 'unclassified'}`,
    sourceField: result.source_field,
    confirmationId: null
  };
}

function mapSanitizedHtml(document: ApiSanitizedHtmlDocument | null): SanitizedHtmlDocument {
  return {
    html: document?.html || '<!doctype html><html><body><article><pre>Projection unavailable.</pre></article></body></html>',
    iframeSandbox: document?.iframe_sandbox || '',
    contentSecurityPolicy: document?.content_security_policy || "default-src 'none'; base-uri 'none'; form-action 'none'; frame-ancestors 'none'"
  };
}

function projectionPendingDocument(): SanitizedHtmlDocument {
  return {
    html: '<!doctype html><html><body><article><pre>Selecting a message loads the backend projection.</pre></article></body></html>',
    iframeSandbox: '',
    contentSecurityPolicy: "default-src 'none'; base-uri 'none'; form-action 'none'; frame-ancestors 'none'"
  };
}

function titleize(value: string): string {
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(' ');
}

function orderedFolders(folders: Record<string, string>): string[] {
  return Object.values(folders).sort((left, right) => {
    if (left === 'INBOX') {
      return -1;
    }
    if (right === 'INBOX') {
      return 1;
    }

    return left.localeCompare(right);
  });
}
